import { metricAliasesById } from "constants/metrics"

import { Button } from "@tokenterminal/core/ui/button/Button"

import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
import { Tabs } from "@tokenterminal/core/ui/navigation/tabs/Tabs"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { useAtom } from "jotai"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import { useEffect, useMemo, useRef, useState } from "react"
import { focusAtom } from "utils/jotai/focusAtom"
import { defaultMetricSortingConfig } from "utils/metricSortingConfig"
import { type FiltersAtomType } from "../ScreenerTable"
import { NON_STATIC_ITEMS, STATIC_ITEMS } from "./constants"
import { SelectableColumnsList } from "./SelectableColumnsList"
import { SelectedColumnsList } from "./SelectedColumnsList"

// Use the category order from the configuration file
const categoryOrder = defaultMetricSortingConfig.categoryOrder || [
  "market",
  "financial",
  "valuation",
  "usage",
  "development",
  "technical",
  "custom",
  "other",
]

export default function ColumnSelectorModal({
  columns,
  filtersAtom,
}: {
  columns: PresetColumnConfig[]
  filtersAtom: FiltersAtomType
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedColumns, setSelectedColumns] = useAtom(
    focusAtom(filtersAtom, "columns")
  )
  const { data: metrics = [] } = useMetricsConfigurationList()

  const metricDictionary = toDictionary(metrics, "metric_id")

  const metricsWithFormattedLabel: Array<{
    label: string
    value: string
    group: string
    keywords?: string[]
    metric_id: string
    static: boolean
  }> = useMemo(() => {
    return metrics
      .map((metric) => {
        const aliases = metricAliasesById[metric.metric_id] || []
        return {
          value: metric.slug,
          group: metric.categories[0] ?? "other",
          label: metric.title,
          keywords: [metric.metric_id, ...aliases],
          metric_id: metric.metric_id,
          static: metricDictionary.get(metric.metric_id)?.static || false,
        }
      })
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.group)
        const bIndex = categoryOrder.indexOf(b.group)

        // If both categories are not in categoryOrder, maintain original order
        if (aIndex === -1 && bIndex === -1) {
          return 0
        }

        // Use -1 as default index for items not in categoryOrder
        return (
          (aIndex === -1 ? categoryOrder.length : aIndex) -
          (bIndex === -1 ? categoryOrder.length : bIndex)
        )
      })
  }, [metrics, metricDictionary])

  const groupMetricsWithFormattedLabel = useMemo(() => {
    const groups = new Map<
      string,
      Array<{
        label: string
        value: string
        metric_id: string
        keywords?: string[]
        static: boolean
      }>
    >()
    metricsWithFormattedLabel.forEach((metric) => {
      if (metric.group) {
        if (!groups.has(metric.group)) {
          groups.set(metric.group, [])
        }

        groups.get(metric.group)?.push({
          ...metric,
        })
      }
    })

    return Array.from(groups.entries()).map(([group, metrics]) => {
      const categoryLower = group.toLowerCase()
      const metricOrder =
        defaultMetricSortingConfig.categoryMetricOrder?.[categoryLower] || []

      return {
        label: group[0].toUpperCase() + group.slice(1),
        value: group,
        children: metrics.sort((a, b) => {
          // If we have a custom order for this category, use it
          if (metricOrder.length > 0) {
            const indexA = metricOrder.indexOf(a.metric_id)
            const indexB = metricOrder.indexOf(b.metric_id)

            // If both metrics are in the custom order, sort by that order
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB
            }

            // If only one metric is in the custom order, prioritize it
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1
          }

          // Default to alphabetical sort
          return a.label.localeCompare(b.label)
        }),
      }
    })
  }, [metricsWithFormattedLabel])

  // Capture the initial selected columns so we can reset to them when the modal is closed
  const defaultSelectedColumns = useRef<PresetColumnConfig[]>(selectedColumns)
  const [draftSelectedColumns, setDraftSelectedColumns] =
    useState<PresetColumnConfig[]>(selectedColumns)

  useEffect(() => {
    if (isOpen) {
      setDraftSelectedColumns(selectedColumns)
    }
  }, [selectedColumns, isOpen])

  const handleAddColumn = (field: string) => {
    setDraftSelectedColumns((prev) => [...prev, { field }])
  }

  const selectedItems = useMemo(() => {
    return draftSelectedColumns.map((c) => {
      const column = columns.find((col) => col.field === c.field)

      const fieldParts = c.field.split("_")

      const aggregation = fieldParts.pop()
      const interval = fieldParts.pop()
      const metric = fieldParts.join("_")

      const metricConfig = metricsWithFormattedLabel.find(
        (m) => m.metric_id === metric
      )

      const isStatic = metricConfig?.static

      const groupOptions = isStatic ? STATIC_ITEMS : NON_STATIC_ITEMS

      return {
        label: metricConfig?.label || column?.header,
        value: c.field,
        group: column?.group,
        sparkline: !!c?.sparkline,
        groupOptions: metric
          ? groupOptions.map((item) => ({
              label: item.label,
              value: `${metric}_${item.value}`,
            }))
          : [],
      }
    })
  }, [columns, draftSelectedColumns, metricsWithFormattedLabel])

  const ungroupedItems = columns.filter((item) => !item.label)

  return (
    <>
      <Button onPress={() => setIsOpen(true)}>Edit columns</Button>
      <Modal
        size="auto"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="max-h-[800px] h-[80vh] flex"
        hasDividers
      >
        <Modal.Header title="Edit columns" subtitle="Projects" />
        <Modal.Body className="flex-1 w-full lg:w-[900px]">
          <div className="gap-5 h-full flex md:hidden">
            <Tabs
              className="w-full"
              tabs={[
                {
                  id: "all",
                  title: "All columns",
                  content: () => (
                    <div className="flex-1 flex flex-col gap-2 w-full">
                      <SearchField
                        placeholder="Search for a column"
                        value={search}
                        onChange={setSearch}
                        aria-label="Search for a column"
                      />
                      <div className="flex-1 overflow-y-auto">
                        <SelectableColumnsList
                          groupedColumns={groupMetricsWithFormattedLabel}
                          ungroupedColumns={ungroupedItems}
                          search={search}
                          handleAddColumn={handleAddColumn}
                          draftSelectedColumns={draftSelectedColumns}
                        />
                      </div>
                    </div>
                  ),
                },
                {
                  id: "selected",
                  title: `Selected columns (${draftSelectedColumns.length + 1})`,
                  content: () => (
                    <div className="flex-[2.5] flex flex-col gap-2 max-h-[100vh]">
                      <SelectedColumnsList
                        setDraftSelectedColumns={setDraftSelectedColumns}
                        selectedItems={selectedItems}
                        draftSelectedColumns={draftSelectedColumns}
                        defaultSelectedColumns={defaultSelectedColumns}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
          <div className="gap-5 h-full hidden md:flex">
            {/* Left column */}
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex items-center justify-between h-[20px] w-full">
                <div className="text-sm font-medium">All columns</div>
              </div>
              <SearchField
                placeholder="Search for a column"
                value={search}
                onChange={setSearch}
                aria-label="Search for a column"
              />
              <div className="flex-1 overflow-y-auto">
                <SelectableColumnsList
                  groupedColumns={groupMetricsWithFormattedLabel}
                  ungroupedColumns={ungroupedItems}
                  search={search}
                  handleAddColumn={handleAddColumn}
                  draftSelectedColumns={draftSelectedColumns}
                />
              </div>
            </div>
            {/* Right column */}
            <div className="flex-[1.75] flex flex-col gap-2 max-h-[100vh]">
              <div className="flex items-center justify-between h-[20px] w-full">
                <div className="text-sm font-medium">
                  Selected columns ({draftSelectedColumns.length + 1})
                </div>
              </div>
              <SelectedColumnsList
                setDraftSelectedColumns={setDraftSelectedColumns}
                selectedItems={selectedItems}
                draftSelectedColumns={draftSelectedColumns}
                defaultSelectedColumns={defaultSelectedColumns}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onPress={() => setIsOpen(false)}>Cancel</Button>
          <Button
            variant="solid"
            onPress={() => {
              setIsOpen(false)

              setTimeout(() => {
                setSelectedColumns(draftSelectedColumns)
              }, 0)
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
